import {
  input,
  Type,
  DestroyRef,
  Directive,
  inject,
  output,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { tap } from 'rxjs';

import { EventEmitterLike, InputSignalLike } from '@core/tellent-types';
import type { DialogComponentI, DialogConfigOptions, DialogRef } from '@core/user-interface-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

@Directive({
  standalone: true,
})
export abstract class DialogWrapComponent<
  C extends DialogComponentI<any, any>,
  O = C extends DialogComponentI<any, infer Result> ? Result : never,
> {
  protected readonly defaultConfig: DialogConfigOptions = {
    width: DialogWidth.Small,
  };
  public open: InputSignalLike<boolean> = input(false);
  public afterClosed: EventEmitterLike<O> = output<O>();

  protected abstract config: DialogConfigOptions;
  protected abstract component: Type<C>;
  protected dialogService = inject(DialogService);
  protected vcRef = inject(ViewContainerRef);
  protected destroyRef = inject(DestroyRef);
  protected dialogRef?: DialogRef;

  public ngOnChanges() {
    this.open() ? this.openDialog() : this.closeDialog();
  }

  public async resolveDialogData(): Promise<any> {
    return Promise.resolve(undefined);
  }

  protected async openDialog() {
    if (this.dialogRef) return;
    const config = {
      ...this.config,
      ...this.defaultConfig,
    };
    const dialogData = await this.resolveDialogData();
    this.dialogRef = this.dialogService.openCustom(
      <Type<DialogComponentI<any, any>>>this.component,
      {
        ...config,
        viewContainerRef: this.vcRef,
      },
      dialogData,
    );
    this.dialogRef.afterClosed
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.dialogRef = undefined;
        }),
      )
      .subscribe(value => this.afterClosed.emit(value));
  }

  protected closeDialog() {
    if (!this.dialogRef) return;
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  protected ngOnDestroy() {
    this.closeDialog();
  }
}
